import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { User, Edit, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const KaeuferProfilAnsicht = () => {
  const { token } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  const assetklassen = [
    { value: 'lebensmittelmarkt', label: 'Lebensmittelmarkt' },
    { value: 'discounter', label: 'Discounter & Drogerie' },
    { value: 'baumarkt', label: 'Baumarkt' },
    { value: 'fachmarkt', label: 'Fachmarkt' },
    { value: 'fachmarktzentrum', label: 'Fachmarktzentrum' }
  ];

  const zustaende = [
    { value: 'neuwertig', label: 'Neuwertig' },
    { value: 'gut', label: 'Gut' },
    { value: 'renovierungsbedarf', label: 'Renovierungsbedarf' }
  ];

  useEffect(() => {
    fetchProfile();
  }, [token]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyer-profiles/${token}`);
      if (!response.ok) throw new Error('Not found');
      const data = await response.json();
      setProfile(data);
      setFormData({
        assetklassen: data.assetklassen,
        plz_gebiete: data.plz_gebiete.join(', '),
        budget_min: data.budget_min,
        budget_max: data.budget_max,
        wault_min: data.wault_min || '',
        zustand_praeferenz: data.zustand_praeferenz,
        is_active: data.is_active
      });
    } catch (error) {
      toast.error('Profil nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const plz_gebiete = formData.plz_gebiete.split(',').map(p => p.trim()).filter(p => p);
      const updateData = {
        assetklassen: formData.assetklassen,
        plz_gebiete: plz_gebiete,
        budget_min: parseInt(formData.budget_min),
        budget_max: parseInt(formData.budget_max),
        wault_min: formData.wault_min ? parseFloat(formData.wault_min) : null,
        zustand_praeferenz: formData.zustand_praeferenz,
        is_active: formData.is_active
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyer-profiles/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) throw new Error('Update failed');
      const data = await response.json();
      setProfile(data);
      setEditMode(false);
      toast.success('Profil aktualisiert!');
    } catch (error) {
      toast.error('Aktualisierung fehlgeschlagen');
    }
  };


  const handleDelete = async () => {
    if (!window.confirm('Möchten Sie Ihr Suchprofil wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyer-profiles/${token}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Suchprofil erfolgreich gelöscht!');
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Netzwerkfehler');
    }
  };


  const toggleAssetklasse = (value) => {
    setFormData({
      ...formData,
      assetklassen: formData.assetklassen.includes(value) 
        ? formData.assetklassen.filter(a => a !== value)
        : [...formData.assetklassen, value]
    });
  };

  const toggleZustand = (value) => {
    setFormData({
      ...formData,
      zustand_praeferenz: formData.zustand_praeferenz.includes(value)
        ? formData.zustand_praeferenz.filter(z => z !== value)
        : [...formData.zustand_praeferenz, value]
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-gray-50 py-12 flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin" style={{ color: 'var(--navy-600)' }} />
        </main>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-gray-50 py-12">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-4">Profil nicht gefunden</h1>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Ihr Suchprofil" url={`/kaeufer/profil/${token}`} />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--navy-900)' }}>Ihr Suchprofil</h1>
            <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Token: {token}</p>
          </div>

          <Card className="glass-card p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <User className="h-8 w-8" style={{ color: 'var(--gold-600)' }} />
                <div>
                  <h2 className="text-xl font-bold">{profile.email}</h2>
                  <p className="text-sm">Erstellt: {new Date(profile.created_at).toLocaleDateString('de-DE')}</p>
                </div>
              </div>
              <Button onClick={() => setEditMode(!editMode)} variant="outline">
                {editMode ? <><X className="h-4 w-4 mr-2" /> Abbrechen</> : <><Edit className="h-4 w-4 mr-2" /> Bearbeiten</>}
              </Button>
            </div>

            {editMode ? (
              <div className="space-y-6">
                <div>
                  <Label className="mb-3 block">Assetklassen</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {assetklassen.map((ak) => (
                      <div key={ak.value} onClick={() => toggleAssetklasse(ak.value)} className={`p-3 rounded-lg border-2 cursor-pointer ${formData.assetklassen.includes(ak.value) ? 'border-[#D4A853] bg-[#D4A853]/10' : 'border-gray-200'}`}>
                        <Checkbox checked={formData.assetklassen.includes(ak.value)} className="pointer-events-none mr-2" />
                        <span className="text-sm">{ak.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div><Label>PLZ-Gebiete</Label><Input value={formData.plz_gebiete} onChange={(e) => setFormData({...formData, plz_gebiete: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Budget Min</Label><Input type="number" value={formData.budget_min} onChange={(e) => setFormData({...formData, budget_min: e.target.value})} /></div>
                  <div><Label>Budget Max</Label><Input type="number" value={formData.budget_max} onChange={(e) => setFormData({...formData, budget_max: e.target.value})} /></div>
                </div>
                <div><Label>WAULT Min</Label><Input type="number" step="0.1" value={formData.wault_min} onChange={(e) => setFormData({...formData, wault_min: e.target.value})} /></div>
                <div>
                  <Label className="mb-3 block">Zustand</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {zustaende.map((z) => (
                      <div key={z.value} onClick={() => toggleZustand(z.value)} className={`p-3 rounded-lg border-2 cursor-pointer ${formData.zustand_praeferenz.includes(z.value) ? 'border-[#D4A853] bg-[#D4A853]/10' : 'border-gray-200'}`}>
                        <Checkbox checked={formData.zustand_praeferenz.includes(z.value)} className="pointer-events-none mr-2" />
                        <span className="text-sm">{z.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="is_active" checked={formData.is_active} onCheckedChange={(checked) => setFormData({...formData, is_active: checked})} />
                  <Label htmlFor="is_active">Profil aktiv</Label>
                </div>
                <Button onClick={handleUpdate} className="w-full btn-gold-gradient text-white border-0 font-semibold py-4">Speichern</Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <p className="text-sm font-semibold mb-2">Assetklassen</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.assetklassen.map((ak) => <span key={ak} className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>{assetklassen.find(a => a.value === ak)?.label || ak}</span>)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-semibold mb-2">PLZ-Gebiete</p>
                    <p className="font-bold">{profile.plz_gebiete.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">Budget</p>
                    <p className="font-bold">€ {profile.budget_min.toLocaleString('de-DE')} - € {profile.budget_max.toLocaleString('de-DE')}</p>
                  </div>
                  {profile.wault_min && <div><p className="text-sm font-semibold mb-2">WAULT Min</p><p className="font-bold">{profile.wault_min} Jahre</p></div>}
                  <div>
                    <p className="text-sm font-semibold mb-2">Status</p>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${profile.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{profile.is_active ? 'Aktiv' : 'Inaktiv'}</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default KaeuferProfilAnsicht;
